import { Component, inject, signal, computed, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { CheckboxModule } from 'primeng/checkbox';

import {
  MessageTemplateService,
  MessageTemplate,
  MessageTemplateDto,
  TEMPLATE_PLACEHOLDERS,
} from '../../services/message-template';
import { MessageChannel, MESSAGE_CHANNELS } from '../../services/communication-config';
import { DropdownService, DropdownItem } from '../../services/dropdown';
import { ClientService } from '../../services/client';
import { ToastService } from '../../services/toast';
import { PageTitleService } from '../../services/page-title';
import { SendMessageDialog } from '../messaging/send-message-dialog';

@Component({
  selector: 'app-message-templates',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    DialogModule,
    SelectModule,
    TableModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    CheckboxModule,
    SendMessageDialog,
  ],
  templateUrl: './message-templates.html',
  styleUrls: ['./message-templates.css'],
})
export class MessageTemplatesPage implements OnInit {
  private fb = inject(FormBuilder);
  private svc = inject(MessageTemplateService);
  private dropdown = inject(DropdownService);
  private clientSvc = inject(ClientService);
  private toast = inject(ToastService);
  readonly pageTitle = inject(PageTitleService).title;
  private platformId = inject(PLATFORM_ID);

  readonly channels = MESSAGE_CHANNELS;
  readonly placeholders = TEMPLATE_PLACEHOLDERS;
  /** Known template keys (one row per key × channel). The field stays editable for new keys. */
  readonly templateKeys = [
    'OTP_VERIFICATION', 'LOGIN_OTP', 'CLIENT_OTP',
    'KYC_SUBMITTED', 'KYC_APPROVED', 'KYC_REJECTED',
    'PAYMENT_LINK', 'POLICY_ISSUED', 'PAYMENT_RECEIPT', 'COI',
  ];

  /** Body placeholder example — bound (not literal) so Angular doesn't interpolate the {{ }}. */
  readonly bodyPlaceholder =
    'Dear {{Name}}, your {{Company}} OTP is {{Otp}}. It expires in {{Minutes}} minutes.';

  /** `{{Token}}` label for a placeholder chip (kept out of the template to avoid nested {{ }}). */
  tpl(token: string): string {
    return `{{${token}}}`;
  }

  // Sample values for the live preview.
  private readonly sample: Record<string, string> = {
    Company: 'Arogya Insurance', Name: 'Ramesh', Otp: '123456', Minutes: '5',
    Amount: '1499.00', Link: 'https://rzp.io/i/sample',
    PolicyNo: 'PSG-23-25-7000249-00-000', Receipt: 'RCPT-000123', Reason: 'Document mismatch',
  };

  loading = signal(false);
  saving = signal(false);
  editing = signal(false);
  showModal = signal(false);
  rows = signal<MessageTemplate[]>([]);
  keyFilter = signal('');
  channelFilter = signal<MessageChannel | null>(null);
  partnerOptions = signal<DropdownItem[]>([]);
  clientOptions = signal<DropdownItem[]>([]);

  /** A template belongs to ONE audience; the backend rejects both ids being set. */
  readonly scopeTypes = [
    { label: 'Global (default)', value: 'GLOBAL' },
    { label: 'Partner', value: 'PARTNER' },
    { label: 'Client', value: 'CLIENT' },
  ];

  filtered = computed(() => this.rows());

  form: FormGroup = this.fb.group({
    id: [0],
    scopeType: ['GLOBAL'],
    partnerId: [null],
    clientId: [null],
    channel: [null, Validators.required],
    templateKey: ['', Validators.required],
    subject: [null],
    body: ['', Validators.required],
    providerTemplateId: [null],
    providerTemplateLang: [null],
    providerButtonUrlBase: [null],
    isActive: [true],
  });

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.dropdown.partners().subscribe((o) => this.partnerOptions.set(o));
    // Clients aren't in the shared dropdown service, so map the client list here.
    this.clientSvc.cltGetAll().subscribe({
      next: (res: any) =>
        this.clientOptions.set(
          ((res?.data ?? []) as any[]).map((c) => ({
            label: `${c.clientName}${c.clientCode ? ' (' + c.clientCode + ')' : ''}`,
            value: c.id,
          })),
        ),
      error: () => this.clientOptions.set([]),
    });
    this.load();
  }

  // ===================== list =====================
  load() {
    this.loading.set(true);
    this.svc.getAll(this.keyFilter().trim() || null, this.channelFilter()).subscribe({
      next: (res: any) => {
        this.loading.set(false);
        const data = res?.data ?? res ?? [];
        this.rows.set(Array.isArray(data) ? data : []);
      },
      error: () => this.loading.set(false),
    });
  }

  onKeyFilter(value: string) {
    this.keyFilter.set(value ?? '');
    this.load();
  }

  onChannelFilter(value: any) {
    this.channelFilter.set(value === '' || value == null ? null : (value as MessageChannel));
    this.load();
  }

  // ===================== display helpers =====================
  scopeLabel(r: MessageTemplate): string {
    if (r.clientId != null) {
      const c = this.clientOptions().find((o) => +o.value === r.clientId);
      return `Client — ${c ? c.label : '#' + r.clientId}`;
    }
    if (r.partnerId == null) return 'Global';
    if (r.partnerName) return r.partnerName;
    const opt = this.partnerOptions().find((o) => +o.value === r.partnerId);
    return `Partner — ${opt ? opt.label : '#' + r.partnerId}`;
  }

  get selectedChannel(): MessageChannel | null {
    return this.form.get('channel')?.value ?? null;
  }

  get isEmail(): boolean {
    return this.selectedChannel === 'EMAIL';
  }

  get isWhatsApp(): boolean {
    return this.selectedChannel === 'WHATSAPP';
  }

  /** Provider template identity applies to WhatsApp (Meta name) and SMS (DLT id) only. */
  get hasProviderTemplate(): boolean {
    const c = this.selectedChannel;
    return c === 'WHATSAPP' || c === 'SMS';
  }

  /** OTP-shaped keys (OTP_VERIFICATION, LOGIN_OTP, CLIENT_OTP...) — Meta's AUTHENTICATION
   *  category has a fixed body taking exactly one parameter: the code. */
  get isOtpKey(): boolean {
    const key = ((this.form.get('templateKey')?.value ?? '') as string).trim().toUpperCase();
    return /(^|_)OTP(_|$)/.test(key);
  }

  /** Distinct placeholders in the body, in FIRST-APPEARANCE order — exactly the {{1}}..{{n}}
   *  parameter list the server derives and sends to the provider. */
  get bodyParams(): string[] {
    const body = (this.form.get('body')?.value ?? '') as string;
    const names: string[] = [];
    for (const m of body.matchAll(/\{\{\s*(\w+)\s*\}\}/g)) {
      if (!names.includes(m[1])) names.push(m[1]);
    }
    return names;
  }

  /** TOTAL {{...}} occurrences in the body — the OTP rule counts occurrences, not names. */
  get bodyPlaceholderCount(): number {
    const body = (this.form.get('body')?.value ?? '') as string;
    return [...body.matchAll(/\{\{\s*\w+\s*\}\}/g)].length;
  }

  /** A template carries its link in the body OR on a button — never both, so the button
   *  base field only shows while the body has no {{Link}}. */
  get bodyHasLink(): boolean {
    return this.bodyParams.includes('Link');
  }

  /** Non-blocking dialog warning: without an approved template name, WhatsApp only delivers
   *  inside a 24h customer-service window — business-initiated sends reach almost nobody. */
  get whatsAppNeedsTemplateName(): boolean {
    return this.isWhatsApp && !((this.form.get('providerTemplateId')?.value ?? '').toString().trim());
  }

  // ===================== preview / placeholders =====================
  /** Substitute sample values into a template string. */
  private render(text: string): string {
    return (text ?? '').replace(/\{\{\s*(\w+)\s*\}\}/g, (m, k) =>
      this.sample[k] != null ? this.sample[k] : m,
    );
  }

  get previewSubject(): string {
    return this.render(this.form.get('subject')?.value ?? '');
  }

  get previewBody(): string {
    return this.render(this.form.get('body')?.value ?? '');
  }

  /** Placeholders present in the body that aren't in the known set (warn, don't block). */
  get unknownPlaceholders(): string[] {
    const body = (this.form.get('body')?.value ?? '') as string;
    const subject = (this.form.get('subject')?.value ?? '') as string;
    const known = new Set<string>(this.placeholders as readonly string[]);
    const found = new Set<string>();
    for (const text of [body, subject]) {
      for (const m of text.matchAll(/\{\{\s*(\w+)\s*\}\}/g)) {
        if (!known.has(m[1])) found.add(m[1]);
      }
    }
    return [...found];
  }

  /** Insert a {{placeholder}} at the caret in the body textarea. */
  insertPlaceholder(token: string, el: HTMLTextAreaElement) {
    const ph = `{{${token}}}`;
    const ctrl = this.form.get('body');
    const cur: string = ctrl?.value ?? '';
    const start = el.selectionStart ?? cur.length;
    const end = el.selectionEnd ?? cur.length;
    ctrl?.setValue(cur.slice(0, start) + ph + cur.slice(end));
    setTimeout(() => {
      const pos = start + ph.length;
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  }

  // ===================== add / edit =====================
  openAdd() {
    this.editing.set(false);
    this.form.reset({
      id: 0,
      scopeType: 'GLOBAL',
      partnerId: null,
      clientId: null,
      channel: this.channelFilter() ?? null,
      templateKey: this.keyFilter().trim() || '',
      subject: null,
      body: '',
      providerTemplateId: null,
      providerTemplateLang: null,
      providerButtonUrlBase: null,
      isActive: true,
    });
    this.showModal.set(true);
  }

  openEdit(r: MessageTemplate) {
    this.editing.set(true);
    this.svc.getById(r.id).subscribe({
      next: (res: any) => {
        const d: MessageTemplate = res?.data ?? r;
        this.form.reset({
          id: d.id,
          scopeType: d.clientId != null ? 'CLIENT' : d.partnerId != null ? 'PARTNER' : 'GLOBAL',
          partnerId: d.partnerId ?? null,
          clientId: d.clientId ?? null,
          channel: d.channel,
          templateKey: d.templateKey ?? '',
          subject: d.subject ?? null,
          body: d.body ?? '',
          providerTemplateId: d.providerTemplateId ?? null,
          providerTemplateLang: d.providerTemplateLang ?? null,
          providerButtonUrlBase: d.providerButtonUrlBase ?? null,
          isActive: d.isActive ?? true,
        });
        this.showModal.set(true);
      },
      error: () => this.toast.error('Could not load the template to edit.'),
    });
  }

  closeModal() {
    this.showModal.set(false);
  }

  /** Switching scope clears the other side, so a stale id is never submitted. */
  onScopeChange() {
    const t = this.form.get('scopeType')?.value;
    if (t !== 'PARTNER') this.form.get('partnerId')?.setValue(null);
    if (t !== 'CLIENT') this.form.get('clientId')?.setValue(null);
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      if (this.form.get('channel')?.invalid) this.toast.warning('Channel is required.');
      else if (this.form.get('templateKey')?.invalid) this.toast.warning('Template key is required.');
      else if (this.form.get('body')?.invalid) this.toast.warning('Body is required.');
      else this.toast.warning('Please complete the required fields.');
      return;
    }

    const v = this.form.getRawValue();

    // Subject is required for EMAIL only.
    if (this.isEmail && !(v.subject ?? '').toString().trim()) {
      this.toast.warning('Subject is required for EMAIL templates.');
      return;
    }

    // Meta's AUTHENTICATION category has a FIXED body taking exactly one parameter (the
    // code). Extra placeholders make the template unapprovable and every send is rejected
    // for a parameter-count mismatch — so the OTP would simply never arrive.
    if (this.isWhatsApp && this.isOtpKey && this.bodyPlaceholderCount !== 1) {
      this.toast.popup(
        'A WhatsApp OTP body must contain exactly one placeholder — the code itself, e.g. ' +
          '"{{Otp}} is your verification code." Expiry and "do not share" lines are rendered ' +
          'by Meta, not typed into the body.',
        'One placeholder only',
        'warning',
      );
      return;
    }

    // The button base is only meaningful for a WhatsApp template whose link lives on a
    // dynamic CTA button — i.e. NOT in the body. It must be an absolute http(s) URL: the
    // server strips it off {{Link}} to build the button parameter Meta appends to the base.
    let buttonBase: string | null = null;
    if (this.isWhatsApp && !this.bodyHasLink) {
      buttonBase = (v.providerButtonUrlBase ?? '').toString().trim() || null;
      if (buttonBase && !/^https?:\/\/.+/i.test(buttonBase)) {
        this.toast.warning(
          'Button base URL must be an absolute http(s) URL, e.g. https://rzp.io/i/ — ' +
            'the base the button was registered with.',
        );
        return;
      }
    }

    const dto: MessageTemplateDto = {
      id: this.editing() ? Number(v.id) : 0,
      partnerId: v.scopeType === 'PARTNER' ? (v.partnerId ?? null) : null,
      clientId: v.scopeType === 'CLIENT' ? (v.clientId ?? null) : null,
      channel: v.channel,
      templateKey: (v.templateKey || '').toString().trim(),
      // Only EMAIL keeps a subject; SMS/WhatsApp send null.
      subject: this.isEmail ? (v.subject ?? '').toString().trim() || null : null,
      body: v.body ?? '',
      providerTemplateId: (v.providerTemplateId ?? '').toString().trim() || null,
      // Language + button base are WhatsApp concepts; other channels store null.
      providerTemplateLang: this.isWhatsApp
        ? (v.providerTemplateLang ?? '').toString().trim() || null
        : null,
      providerButtonUrlBase: buttonBase,
      isActive: !!v.isActive,
    };

    this.saving.set(true);
    this.svc.upsert(dto).subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to save template.');
          return;
        }
        this.toast.success(res?.message || (this.editing() ? 'Template updated' : 'Template created'));
        this.closeModal();
        this.load();
      },
      error: () => this.saving.set(false),
    });
  }

  // ===================== inline active toggle =====================
  toggleActive(r: MessageTemplate) {
    const next = !r.isActive;
    this.patchRow(r.id, { isActive: next });
    this.svc.setActive(r.id, next).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.patchRow(r.id, { isActive: r.isActive });
          this.toast.error(res?.message || 'Could not update status.');
        }
      },
      error: () => {
        this.patchRow(r.id, { isActive: r.isActive });
        this.toast.error('Could not update status.');
      },
    });
  }

  private patchRow(id: number, patch: Partial<MessageTemplate>) {
    this.rows.update((list) => list.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  }

  // ===================== delete =====================
  async remove(r: MessageTemplate) {
    const ok = await this.toast.confirm(
      `${r.templateKey} (${r.channel}, ${this.scopeLabel(r)}) will be permanently deleted. This cannot be undone.`,
      'Delete message template?',
      'Delete',
    );
    if (!ok) return;
    this.svc.delete(r.id).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to delete.');
          return;
        }
        this.toast.success(res?.message || 'Template deleted');
        this.load();
      },
    });
  }
}
